import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { Trophy, Users, Calendar, TrendingUp, ChevronRight, Target, Crown, Swords, BarChart3, Zap, Star, Globe, GitBranch, Radio, BookOpen } from 'lucide-react';
import { MainLayout } from '@/components/layout/MainLayout';
import { SEOHead } from '@/components/SEOHead';
import { UpcomingMatchesWidget } from '@/components/UpcomingMatchesWidget';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useLiveEnabled } from '@/hooks/useLiveEnabled';
import vsuLogo from '@/assets/vsu-logo.jpg';

// New Components
import { AnnouncementTicker } from '@/components/home/AnnouncementTicker';
import { LiveMatchHighlight } from '@/components/home/LiveMatchHighlight';
import { FeaturedPlayerCard } from '@/components/home/FeaturedPlayerCard';
import { TrophyShowcase } from '@/components/home/TrophyShowcase';
import { RotatingSlogan } from '@/components/home/RotatingSlogan';
import { HeroParticles } from '@/components/home/HeroParticles';
import { VirtualTrophyHouse } from '@/components/home/VirtualTrophyHouse';
import { LiveMatchWidget } from '@/components/home/LiveMatchWidget';
import { LiveMatchSection } from '@/components/home/LiveMatchSection';

import { AdminPreviewWidgets } from '@/components/home/AdminPreviewWidgets';
import { AnimatedStatsCounter } from '@/components/home/AnimatedStatsCounter';
import { SocialFeedPreview } from '@/components/home/SocialFeedPreview';
import { TopPlayersShowcase } from '@/components/home/TopPlayersShowcase';
import { PlayerRanking } from '@/components/home/PlayerRanking';
import { NewsSection } from '@/components/home/NewsSection';
import { JoinCommunityCTA } from '@/components/home/JoinCommunityCTA';
import { TodayMatchesTicker } from '@/components/home/TodayMatchesTicker';

// Feature Components
import { 
  HeadToHeadWidget, 
  MatchPrediction, 
  WeeklyChallenges, 
  FanPolls, 
  PlayerStreaks, 
  MatchHighlightsFeed,
  ClubAnniversary 
} from '@/components/features';

interface Stats {
  totalPlayers: number;
  activeTournaments: number;
  completedMatches: number;
  upcomingMatches: number;
}

interface TopPlayer {
  id: string;
  name: string;
  slug: string;
  avatar: string | null;
  points: number;
  wins: number;
  goals: number;
  gameId?: string | null;
  matches?: number;
  draws?: number;
  losses?: number;
  rankChange?: number; // Positive = moved up, Negative = moved down, 0 = same, undefined = new
  previousRank?: number;
  overall_rating?: number | null;
  skill_ratings?: {
    pace?: number;
    shooting?: number;
    passing?: number;
    dribbling?: number;
    defending?: number;
    physical?: number;
  } | null;
  position?: string | null;
  jersey_number?: number | null;
  skills_manual_override?: boolean;
}

interface PlayerInfo {
  id: string;
  name: string;
  avatar: string | null;
  slug: string;
}

interface RecentMatch {
  id: string;
  team_a_name: string | null;
  team_b_name: string | null;
  score_a: number | null;
  score_b: number | null;
  match_date: string | null;
  status: string;
}

interface TodayMatch {
  id: string;
  team_a_name: string | null;
  team_b_name: string | null;
  score_a: number | null;
  score_b: number | null;
  match_date: string | null;
  status: string;
  player_a: PlayerInfo | null;
  player_b: PlayerInfo | null;
}

interface ActiveTournament {
  id: string;
  name: string;
  status: string;
  start_date: string | null;
}

interface SeasonStats {
  totalWins: number;
  totalDraws: number;
  totalGoals: number;
}

interface Announcement {
  id: string;
  text: string;
  link?: string;
}

interface TrophyItem {
  id: string;
  tournamentName: string;
  position: 'champion' | 'runner-up' | 'third';
  season?: string;
}

export default function Index() {
  const { user, isAdmin, isEditor } = useAuth();
  const { isEnabled: liveEnabled, isLoading: liveEnabledLoading } = useLiveEnabled();
  const [stats, setStats] = useState<Stats>({
    totalPlayers: 0,
    activeTournaments: 0,
    completedMatches: 0,
    upcomingMatches: 0,
  });
  const [topPlayers, setTopPlayers] = useState<TopPlayer[]>([]);
  const [featuredPlayer, setFeaturedPlayer] = useState<TopPlayer | null>(null);
  const [recentMatches, setRecentMatches] = useState<RecentMatch[]>([]);
  const [activeTournaments, setActiveTournaments] = useState<ActiveTournament[]>([]);
  const [seasonStats, setSeasonStats] = useState<SeasonStats>({ totalWins: 0, totalDraws: 0, totalGoals: 0 });
  const [todayMatches, setTodayMatches] = useState<TodayMatch[]>([]);
  const [liveMatch, setLiveMatch] = useState<RecentMatch | null>(null);
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [trophies, setTrophies] = useState<TrophyItem[]>([]);
  const [newsItems, setNewsItems] = useState<{ id: string; title: string; content: string; image_url?: string | null; created_at: string; is_published?: boolean }[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const today = new Date().toISOString().split('T')[0];

        const [playersRes, tournamentsRes, completedRes, upcomingRes] = await Promise.all([
          supabase.from('players').select('id', { count: 'exact', head: true }).eq('is_active', true),
          supabase.from('tournaments').select('id', { count: 'exact', head: true }).eq('status', 'active'),
          supabase.from('matches').select('id', { count: 'exact', head: true }).eq('status', 'completed'),
          supabase.from('matches').select('id', { count: 'exact', head: true }).eq('status', 'scheduled'),
        ]);

        setStats({
          totalPlayers: playersRes.count || 0,
          activeTournaments: tournamentsRes.count || 0,
          completedMatches: completedRes.count || 0,
          upcomingMatches: upcomingRes.count || 0,
        });

        // Fetch Player of the Week from dedicated table first
        const { data: potwData } = await supabase
          .from('player_of_the_week')
          .select('*, player:players(id, name, slug, avatar, position, jersey_number, overall_rating, skill_ratings, skills_manual_override, profile_data:profiles!players_profile_id_fkey(game_id))')
          .order('week_end', { ascending: false })
          .limit(1)
          .maybeSingle();

        // Fetch top 5 players for leaderboard with player details and profile data
        // Use consistent tie-breaking: points DESC, wins DESC, goals_for DESC, goals_against ASC, player_id ASC (final tiebreaker)
        const { data: leaderboardData } = await supabase
          .from('leaderboard')
          .select('player_id, points, wins, draws, losses, matches_played, goals_for, goals_against, player:players(id, name, slug, avatar, position, jersey_number, overall_rating, skill_ratings, skills_manual_override, profile_data:profiles!players_profile_id_fkey(game_id))')
          .eq('period_type', 'all_time')
          .order('points', { ascending: false })
          .order('wins', { ascending: false })
          .order('goals_for', { ascending: false })
          .order('goals_against', { ascending: true })
          .order('player_id', { ascending: true })
          .limit(10); // Fetch more to calculate rank changes

        // Fetch weekly leaderboard for current week's top performer
        const { data: weeklyLeaderboardData } = await supabase
          .from('leaderboard')
          .select('player_id, points, wins, draws, losses, matches_played, goals_for, goals_against, rank, player:players(id, name, slug, avatar, position, jersey_number, overall_rating, skill_ratings, skills_manual_override, profile_data:profiles!players_profile_id_fkey(game_id))')
          .eq('period_type', 'weekly')
          .order('points', { ascending: false })
          .limit(20);

        // Create a map of weekly rankings
        const weeklyRankMap = new Map<string, number>();
        weeklyLeaderboardData?.forEach((entry, index) => {
          weeklyRankMap.set(entry.player_id, index + 1);
        });

        if (leaderboardData) {
          const players = leaderboardData
            .filter(entry => entry.player)
            .map((entry, currentRankIndex) => {
              const playerData = entry.player as any;
              const currentRank = currentRankIndex + 1;
              const previousRank = weeklyRankMap.get(entry.player_id);
              
              // Calculate rank change (positive = improved, negative = dropped)
              let rankChange: number | undefined;
              if (previousRank !== undefined) {
                rankChange = previousRank - currentRank; // If was 5th, now 3rd = +2 (moved up)
              }
              
              return {
                id: entry.player_id,
                name: playerData?.name || 'Unknown',
                slug: playerData?.slug || '',
                avatar: playerData?.avatar || null,
                points: entry.points || 0,
                wins: entry.wins || 0,
                goals: entry.goals_for || 0,
                gameId: playerData?.profile_data?.game_id || null,
                matches: entry.matches_played || 0,
                draws: entry.draws || 0,
                losses: entry.losses || 0,
                overall_rating: playerData?.overall_rating || null,
                skill_ratings: playerData?.skill_ratings || null,
                position: playerData?.position || null,
                jersey_number: playerData?.jersey_number || null,
                skills_manual_override: playerData?.skills_manual_override || false,
                rankChange,
                previousRank,
              };
            });
          
          setTopPlayers(players);
          
          // Set featured player: Priority order:
          // 1. Most recent Player of the Week from dedicated table
          // 2. Current week's top player from weekly leaderboard
          // 3. All-time top player as fallback
          if (potwData && potwData.player) {
            const potwPlayer = potwData.player as any;
            setFeaturedPlayer({
              id: potwData.player_id,
              name: potwPlayer.name || 'Unknown',
              slug: potwPlayer.slug || '',
              avatar: potwPlayer.avatar || null,
              points: potwData.points || 0,
              wins: potwData.wins || 0,
              goals: potwData.goals_for || 0,
              gameId: potwPlayer.profile_data?.game_id || null,
              matches: potwData.matches_played || 0,
              draws: potwData.draws || 0,
              losses: potwData.losses || 0,
              overall_rating: potwPlayer.overall_rating || null,
              skill_ratings: potwPlayer.skill_ratings || null,
              position: potwPlayer.position || null,
              jersey_number: potwPlayer.jersey_number || null,
              skills_manual_override: potwPlayer.skills_manual_override || false,
            });
          } else if (weeklyLeaderboardData && weeklyLeaderboardData.length > 0 && weeklyLeaderboardData[0].player) {
            // Use current week's top player
            const weeklyTop = weeklyLeaderboardData[0];
            const weeklyPlayer = weeklyTop.player as any;
            setFeaturedPlayer({
              id: weeklyTop.player_id,
              name: weeklyPlayer.name || 'Unknown',
              slug: weeklyPlayer.slug || '',
              avatar: weeklyPlayer.avatar || null,
              points: weeklyTop.points || 0,
              wins: weeklyTop.wins || 0,
              goals: weeklyTop.goals_for || 0,
              gameId: weeklyPlayer.profile_data?.game_id || null,
              matches: weeklyTop.matches_played || 0,
              draws: weeklyTop.draws || 0,
              losses: weeklyTop.losses || 0,
              overall_rating: weeklyPlayer.overall_rating || null,
              skill_ratings: weeklyPlayer.skill_ratings || null,
              position: weeklyPlayer.position || null,
              jersey_number: weeklyPlayer.jersey_number || null,
              skills_manual_override: weeklyPlayer.skills_manual_override || false,
            });
          } else if (players.length > 0) {
            // Fallback to all-time top
            setFeaturedPlayer(players[0]);
          }
        }

        // Fetch recent completed matches
        const { data: recentMatchesData } = await supabase
          .from('matches')
          .select('id, team_a_name, team_b_name, score_a, score_b, match_date, status')
          .eq('status', 'completed')
          .order('match_date', { ascending: false })
          .order('id', { ascending: true })
          .limit(5);

        if (recentMatchesData) {
          setRecentMatches(recentMatchesData);
        }

        // Fetch today's matches with player data
        const { data: todayMatchesData } = await supabase
          .from('matches')
          .select(`
            id, team_a_name, team_b_name, score_a, score_b, match_date, status,
            player_a:players!matches_team_a_fkey(id, name, avatar, slug),
            player_b:players!matches_team_b_fkey(id, name, avatar, slug)
          `)
          .gte('match_date', `${today}T00:00:00`)
          .lte('match_date', `${today}T23:59:59`)
          .order('match_date', { ascending: true })
          .order('id', { ascending: true });

        if (todayMatchesData) {
          setTodayMatches(todayMatchesData as TodayMatch[]);
          
          // Check for live match
          const live = todayMatchesData.find(m => m.status === 'live');
          if (live) {
            setLiveMatch(live as RecentMatch);
          } else if (todayMatchesData.length > 0) {
            // Show first today's match if no live match
            setLiveMatch(todayMatchesData[0] as RecentMatch);
          }
        }

        // Fetch active tournaments
        const { data: activeTournamentsData } = await supabase
          .from('tournaments')
          .select('id, name, status, start_date')
          .eq('status', 'active')
          .limit(3);

        if (activeTournamentsData) {
          setActiveTournaments(activeTournamentsData);
        }

        // Fetch completed tournaments for trophy showcase
        const { data: completedTournaments } = await supabase
          .from('tournaments')
          .select('id, name, status')
          .eq('status', 'completed')
          .order('end_date', { ascending: false })
          .order('id', { ascending: true })
          .limit(5);

        if (completedTournaments) {
          setTrophies(completedTournaments.map((t, i) => ({
            id: t.id,
            tournamentName: t.name,
            position: i === 0 ? 'champion' : i === 1 ? 'runner-up' : 'third' as const,
          })));
        }

        // Calculate season stats from leaderboard
        const { data: allLeaderboard } = await supabase
          .from('leaderboard')
          .select('wins, draws, goals_for')
          .eq('period_type', 'all_time');

        if (allLeaderboard) {
          const totalWins = allLeaderboard.reduce((sum, entry) => sum + (entry.wins || 0), 0);
          const totalDraws = allLeaderboard.reduce((sum, entry) => sum + (entry.draws || 0), 0);
          const totalGoals = allLeaderboard.reduce((sum, entry) => sum + (entry.goals_for || 0), 0);
          setSeasonStats({ totalWins, totalDraws, totalGoals });
        }

        // Fetch announcements from site settings
        const { data: announcementsSetting } = await supabase
          .from('site_settings')
          .select('value')
          .eq('key', 'announcements')
          .maybeSingle();

        if (announcementsSetting?.value) {
          const announcementsArray = Array.isArray(announcementsSetting.value) 
            ? announcementsSetting.value 
            : [];
          setAnnouncements(announcementsArray as unknown as Announcement[]);
        }

        // Fetch news from news table
        const { data: newsData } = await supabase
          .from('news')
          .select('id, title, content, image_url, created_at, is_published')
          .eq('is_published', true)
          .order('created_at', { ascending: false })
          .limit(4);

        if (newsData) {
          setNewsItems(newsData);
        }

      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();

    // Real-time subscription - only on desktop for performance
    const isMobile = window.innerWidth < 768;
    let channel: ReturnType<typeof supabase.channel> | null = null;
    
    if (!isMobile) {
      channel = supabase
        .channel('homepage-realtime')
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table: 'players' },
          () => { fetchData(); }
        )
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table: 'leaderboard' },
          () => { fetchData(); }
        )
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table: 'matches' },
          () => { fetchData(); }
        )
        .subscribe();
    }

    return () => {
      if (channel) {
        supabase.removeChannel(channel);
      }
    };
  }, []);

  // Build quick nav items - Dashboard is conditional based on user login and role
  // Live Match is conditional based on live_match_enabled setting
  const quickNavItems = [
    // Dashboard only shown when user is logged in
    ...(user ? [{
      icon: BarChart3, 
      label: 'Dashboard', 
      sublabel: (isAdmin || isEditor) ? 'Admin Panel' : 'My Profile', 
      href: (isAdmin || isEditor) ? '/admin' : '/dashboard', 
      colorClass: 'text-primary', 
      cardClass: 'squad'
    }] : []),
    { icon: Users, label: 'Players', sublabel: 'View roster', href: '/players', colorClass: 'text-secondary', cardClass: 'tournament' },
    // Live Match only shown when live system is enabled
    ...(liveEnabled ? [{ icon: Radio, label: 'Live Match', sublabel: 'Watch live', href: '/live', colorClass: 'text-red-500', cardClass: 'live' }] : []),
    { icon: Trophy, label: 'Tournaments', sublabel: 'Competitions', href: '/tournaments', colorClass: 'text-gold', cardClass: 'leaderboard' },
    { icon: Target, label: 'Matches', sublabel: 'View results', href: '/matches', colorClass: 'text-secondary', cardClass: 'tournament' },
    { icon: GitBranch, label: 'Division', sublabel: 'League & Rankings', href: '/division', colorClass: 'text-purple-400', cardClass: 'squad' },
    { icon: Globe, label: 'External Match', sublabel: 'Club vs Club', href: '/external-matches', colorClass: 'text-accent', cardClass: 'tournament' },
  ];

  const featureCards = [
    { icon: Users, label: 'Squad Management', sublabel: 'Manage main and academy rosters', href: '/squads', colorClass: 'text-primary', cardClass: 'squad' },
    { icon: Trophy, label: 'Tournaments', sublabel: 'Create and track competitions', href: '/tournaments', colorClass: 'text-gold', cardClass: 'leaderboard' },
    { icon: Target, label: 'Match Results', sublabel: 'Auto-update leaderboards', href: '/matches', colorClass: 'text-secondary', cardClass: 'tournament' },
  ];

  const isEmpty = stats.totalPlayers === 0 && stats.activeTournaments === 0 && stats.completedMatches === 0;

  return (
    <MainLayout>
      <SEOHead 
        title="Viper Strike United – Bangladesh eFootball Mobile Gaming Club"
        description="Viper Strike United is Bangladesh's premier eFootball Mobile gaming club. Compete, improve skills, and dominate the virtual pitch with the VSU community."
        canonical="https://viperstrikeunited.xyz/"
        ogImage="https://viperstrikeunited.xyz/logo.png"
      />
      
      {/* Announcement Ticker */}
      <AnnouncementTicker announcements={announcements} />
      
      
      {/* Hero Section */}
      <section className="relative pt-0 pb-8 md:py-24 overflow-hidden hero-gradient">
        {/* Dynamic Background Effects */}
        <div className="absolute inset-0 hexagon-pattern opacity-50"></div>
        <div className="absolute inset-0 grid-pattern"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] glow-gradient pointer-events-none animate-pulse-slow"></div>
        
        {/* Floating Particles */}
        <HeroParticles />
        
        {/* Animated gradient orbs - hidden on mobile for performance */}
        <div className="absolute top-20 left-10 w-64 h-64 bg-primary/5 rounded-full blur-3xl animate-float hidden md:block"></div>
        <div className="absolute bottom-20 right-10 w-80 h-80 bg-secondary/5 rounded-full blur-3xl animate-float hidden md:block" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-1/3 right-1/4 w-32 h-32 bg-gold/5 rounded-full blur-2xl animate-float hidden md:block" style={{ animationDelay: '2s' }}></div>
        
        <div className="container relative z-10 px-3 md:px-4">
          <div className="max-w-4xl mx-auto text-center pt-6 md:pt-4">
            {/* Badge */}
            <div className="inline-flex items-center gap-1 rounded-full border border-primary/40 bg-primary/10 backdrop-blur-sm px-1.5 py-0.5 md:px-4 md:py-1.5 mb-2 md:mb-8 animate-glow">
              <img src={vsuLogo} alt="VSU" className="h-3 w-3 md:h-5 md:w-5 rounded-full ring-1 ring-primary/30" />
              <span className="text-[7px] md:text-xs font-semibold text-primary uppercase tracking-wider">eFootball Mobile</span>
              <Zap className="h-2 w-2 md:h-3 md:w-3 text-primary" />
            </div>

            {/* Main Title - H1 for SEO with Golden & Silver Gaming Colors */}
            <h1 className="font-display text-[4.5rem] xs:text-[5.5rem] sm:text-7xl md:text-8xl mb-5 md:mb-6 leading-[0.85] tracking-tight">
              <span className="block bg-gradient-to-r from-amber-300 via-yellow-400 to-amber-500 bg-clip-text text-transparent drop-shadow-[0_4px_20px_rgba(251,191,36,0.6)] animate-text-glow">VIPER STRIKE</span>
              <span className="block bg-gradient-to-r from-slate-300 via-gray-200 to-zinc-400 bg-clip-text text-transparent drop-shadow-[0_4px_20px_rgba(148,163,184,0.5)] animate-text-glow" style={{ animationDelay: '0.5s' }}>UNITED</span>
            </h1>

            {/* Rotating Slogan */}
            <div className="mb-5 md:mb-0">
              <RotatingSlogan />
            </div>

            {/* Club Rules Button - Only visible to logged in users */}
            {user && (
              <div className="flex flex-wrap items-center justify-center gap-3 mt-5 md:mt-6">
                <Button asChild size="sm" className="btn-gaming text-primary-foreground px-4 md:px-6 text-xs md:text-sm">
                  <Link to="/terms">
                    <BookOpen className="mr-1.5 h-3.5 w-3.5 md:h-4 md:w-4" />
                    Club Rules
                  </Link>
                </Button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Admin Preview Widgets */}
      {(isAdmin || isEditor) && (
        <AdminPreviewWidgets 
          hasPlayers={stats.totalPlayers > 0}
          hasTournaments={stats.activeTournaments > 0}
          hasMatches={stats.completedMatches > 0 || stats.upcomingMatches > 0}
        />
      )}

      {/* Live Match Widget */}
      <LiveMatchWidget />

      {/* Trophy Showcase */}
      <TrophyShowcase trophies={trophies} />


      {/* Top 3 Players Showcase - Always visible with 3 slots */}
      <TopPlayersShowcase players={topPlayers} />

      {/* News Section - After Top Players */}
      <NewsSection news={newsItems} />
      
      {/* Player Ranking - A to Z with full stats */}
      <PlayerRanking />

      {/* Quick Access Grid */}
      <section className="py-6 md:py-8 relative z-20">
        <div className="container px-3 md:px-4">
          <h2 className="text-center text-sm md:text-lg font-medium text-muted-foreground mb-4 md:mb-6">Quick Access</h2>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2 md:gap-4">
            {quickNavItems.map((item, index) => (
              <Link 
                key={index} 
                to={item.href}
                className={`quick-nav-card ${item.cardClass} p-3 md:p-6 text-center group rounded-xl md:rounded-2xl animate-fade-in`}
                style={{ animationDelay: `${index * 80}ms`, animationFillMode: 'backwards' }}
              >
                <div className={`inline-flex items-center justify-center w-9 h-9 md:w-14 md:h-14 rounded-lg md:rounded-2xl bg-muted/50 mb-2 md:mb-4 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300`}>
                  <item.icon className={`h-4 w-4 md:h-7 md:w-7 ${item.colorClass} group-hover:animate-pulse`} />
                </div>
                <div className="font-display text-xs md:text-lg text-foreground mb-0 md:mb-1 truncate">{item.label}</div>
                <div className="text-[9px] md:text-xs text-muted-foreground truncate hidden sm:block">{item.sublabel}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Feature Cards */}
      <section className="py-4 md:py-6 relative z-20">
        <div className="container px-3 md:px-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
            {featureCards.map((item, index) => (
              <Link 
                key={index} 
                to={item.href}
                className={`quick-nav-card ${item.cardClass} p-4 md:p-5 flex items-center gap-3 md:gap-4 group rounded-xl animate-fade-in`}
                style={{ animationDelay: `${(quickNavItems.length * 80) + (index * 100)}ms`, animationFillMode: 'backwards' }}
              >
                <div className={`inline-flex items-center justify-center w-10 h-10 md:w-12 md:h-12 rounded-lg md:rounded-xl bg-muted/50 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300 flex-shrink-0`}>
                  <item.icon className={`h-5 w-5 md:h-6 md:w-6 ${item.colorClass} group-hover:animate-pulse`} />
                </div>
                <div className="min-w-0">
                  <div className="font-display text-sm md:text-base text-foreground mb-0.5 truncate">{item.label}</div>
                  <div className="text-[10px] md:text-xs text-muted-foreground truncate">{item.sublabel}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Virtual Trophy House */}
      <VirtualTrophyHouse />

      {/* Live Match Section */}
      <LiveMatchSection />

      {/* Show Modern Features when empty or always show top players */}
      {isEmpty && !isLoading && (
        <>
          {/* Top 3 Players Showcase */}
          <TopPlayersShowcase players={topPlayers} />
          
          {/* Player Ranking - A to Z with full stats */}
          <PlayerRanking />
          
          {/* News & Announcements */}
          <NewsSection news={newsItems} />
          
          {/* Season Stats with Animated Counters */}
          <section className="py-8">
            <div className="container">
              <Card variant="glass">
                <CardHeader>
                  <CardTitle className="flex items-center gap-3">
                    <div className="p-2 rounded-xl bg-gold/10">
                      <TrendingUp className="h-5 w-5 text-gold" />
                    </div>
                    Season Statistics
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <AnimatedStatsCounter 
                    totalWins={seasonStats.totalWins}
                    totalDraws={seasonStats.totalDraws}
                    totalGoals={seasonStats.totalGoals}
                  />
                </CardContent>
              </Card>
            </div>
          </section>
          
          {/* Join Community CTA */}
          <JoinCommunityCTA />
        </>
      )}

      {/* Today's Match Section */}
      {!isEmpty && (
        <section className="py-4 md:py-8">
          <div className="container px-3 md:px-4">
            <TodayMatchesTicker matches={todayMatches} isLoading={isLoading} />
          </div>
        </section>
      )}

      {/* Upcoming Matches Widget */}
      <UpcomingMatchesWidget />

      {/* Featured Player + Recent Matches Row */}
      {!isEmpty && (
        <section className="py-8">
          <div className="container">
            <div className="grid md:grid-cols-3 gap-6">
              {/* Featured Player */}
              <FeaturedPlayerCard player={featuredPlayer} />

              {/* Recent Matches - Cyan/Secondary color - Hidden for admins */}
              {!(isAdmin || isEditor) && (
                <Card variant="tournament" className="md:col-span-2 overflow-hidden">
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between">
                      <CardTitle className="flex items-center gap-3">
                        <div className="p-2 rounded-xl bg-secondary/10">
                          <Swords className="h-5 w-5 text-secondary" />
                        </div>
                        Recent Matches
                      </CardTitle>
                      <Button asChild variant="ghost" size="sm" className="hover:bg-secondary/10 hover:text-secondary">
                        <Link to="/matches">
                          View All
                          <ChevronRight className="h-4 w-4 ml-1" />
                        </Link>
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {isLoading ? (
                      <div className="space-y-3">
                        {[1,2,3].map(i => (
                          <div key={i} className="animate-shimmer h-14 rounded-xl bg-muted/20"></div>
                        ))}
                      </div>
                    ) : recentMatches.length > 0 ? (
                      <div className="space-y-2">
                        {recentMatches.map((match) => {
                          const scoreA = match.score_a ?? 0;
                          const scoreB = match.score_b ?? 0;
                          const isWin = scoreA > scoreB;
                          const isDraw = scoreA === scoreB;
                          
                          return (
                            <div key={match.id} className="flex items-center justify-between p-4 rounded-xl bg-muted/20 border border-border/20 hover:border-secondary/30 transition-colors">
                              <span className="text-sm font-medium truncate flex-1">{match.team_a_name || 'TBD'}</span>
                              <span className={`font-mono text-lg px-4 tabular-nums ${
                                isWin ? 'text-success' : isDraw ? 'text-warning' : 'text-loss'
                              }`}>
                                {scoreA} - {scoreB}
                              </span>
                              <span className="text-sm font-medium truncate flex-1 text-right">{match.team_b_name || 'TBD'}</span>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-10 text-muted-foreground">
                        <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-muted/30 mb-3">
                          <Swords className="h-7 w-7 opacity-40" />
                        </div>
                        <p className="text-sm font-medium">No recent matches</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Active Cups & Season Stats Row */}
      {!isEmpty && (
        <section className="py-8">
          <div className="container">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Active Cups - Gold/Warning color */}
              <Card variant="leaderboard" className="overflow-hidden">
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-3">
                      <div className="p-2 rounded-xl bg-gold/10">
                        <Trophy className="h-5 w-5 text-gold" />
                      </div>
                      Active Tournaments
                    </CardTitle>
                    <Button asChild variant="ghost" size="sm" className="hover:bg-gold/10 hover:text-gold">
                      <Link to="/tournaments">
                        View All
                        <ChevronRight className="h-4 w-4 ml-1" />
                      </Link>
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {isLoading ? (
                    <div className="space-y-3">
                      {[1,2].map(i => (
                        <div key={i} className="animate-shimmer h-20 rounded-xl bg-muted/20"></div>
                      ))}
                    </div>
                  ) : activeTournaments.length > 0 ? (
                    <div className="space-y-3">
                      {activeTournaments.map((tournament) => (
                        <div key={tournament.id} className="p-5 rounded-xl bg-gold/5 border border-gold/20 hover:border-gold/40 transition-colors">
                          <div className="flex items-center gap-4">
                            <div className="p-2.5 rounded-xl bg-gold/10">
                              <Trophy className="h-6 w-6 text-gold" />
                            </div>
                            <div>
                              <div className="font-semibold text-foreground">{tournament.name}</div>
                              {tournament.start_date && (
                                <div className="text-xs text-muted-foreground mt-1">
                                  Started: {format(new Date(tournament.start_date), 'MMM dd, yyyy')}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-10 text-muted-foreground">
                      <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-muted/30 mb-3">
                        <Trophy className="h-7 w-7 opacity-40" />
                      </div>
                      <p className="text-sm font-medium">No active tournaments</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Season Stats Card - Accent/Purple color with animated counters */}
              <Card variant="external" className="overflow-hidden">
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center gap-3">
                    <div className="p-2 rounded-xl bg-accent/10">
                      <BarChart3 className="h-5 w-5 text-accent" />
                    </div>
                    Season Stats
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <AnimatedStatsCounter 
                    totalWins={seasonStats.totalWins}
                    totalDraws={seasonStats.totalDraws}
                    totalGoals={seasonStats.totalGoals}
                  />
                </CardContent>
              </Card>
            </div>
          </div>
        </section>
      )}

      {/* Leaderboard Section */}
      {!isEmpty && topPlayers.length > 0 && (
        <section className="py-8">
          <div className="container">
            <Card variant="leaderboard" className="overflow-hidden">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-3">
                    <div className="p-2 rounded-xl bg-gold/10">
                      <Crown className="h-5 w-5 text-gold" />
                    </div>
                    Top Players
                  </CardTitle>
                  <Button asChild variant="ghost" size="sm" className="hover:bg-gold/10 hover:text-gold">
                    <Link to="/leaderboard">
                      Full Leaderboard
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Link>
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow className="border-border/30 hover:bg-transparent">
                      <TableHead className="w-12 text-muted-foreground">#</TableHead>
                      <TableHead className="text-muted-foreground">Player</TableHead>
                      <TableHead className="text-right text-muted-foreground">Wins</TableHead>
                      <TableHead className="text-right text-muted-foreground">Points</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {topPlayers.map((player, index) => (
                      <TableRow key={player.id} className="border-border/20 hover:bg-gold/5">
                        <TableCell className="font-medium text-lg">
                          {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : index + 1}
                        </TableCell>
                        <TableCell className="font-medium">{player.name}</TableCell>
                        <TableCell className="text-right font-mono text-success tabular-nums">{player.wins}</TableCell>
                        <TableCell className="text-right font-mono text-xl font-bold text-gold tabular-nums">{player.points}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
        </section>
      )}

      {/* New Features Section */}
      {!isEmpty && (
        <section className="py-8 bg-gradient-to-b from-background to-muted/20">
          <div className="container">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-bold mb-2">🎮 Interactive Features</h2>
              <p className="text-muted-foreground">প্রেডিকশন করুন, ভোট দিন, চ্যালেঞ্জে অংশ নিন!</p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Match Prediction */}
              <MatchPrediction />
              
              {/* Fan Polls - MOTM Voting */}
              <FanPolls />
              
              {/* Head to Head */}
              <HeadToHeadWidget />
              
              {/* Player Streaks */}
              <PlayerStreaks />
              
              {/* Weekly Challenges */}
              <WeeklyChallenges />
              
              {/* Club Anniversary */}
              <ClubAnniversary foundingDate={new Date('2024-02-04')} clubName="VSU" />
            </div>
          </div>
        </section>
      )}

      {/* Match Highlights Feed */}
      {!isEmpty && (
        <section className="py-8">
          <div className="container">
            <div className="max-w-2xl mx-auto">
              <MatchHighlightsFeed />
            </div>
          </div>
        </section>
      )}
    </MainLayout>
  );
}
